import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import core, { API, GQL } from '@gin-betting/core';
import favorite from '@gin-betting/favorite';

import ui, { UI } from 'modules/ui';
import UserDevice from 'services/user-device';
import { getDefaultVariablesFavorite } from 'utils';
import GET_SPORT_EVENT_IDS_BY_FILTERS from './gqlGetSportEventIdsByFilters';

type GetSportEventIdsByFiltersQuery = {
    matches: Array<{
        id: string;
        fixture: {
            type: GQL.SportEventType;
        };
    }>;
};

const {
    appServiceLocator,
    utils: { getBettingOptions },
} = core;
const {
    actions: { changeFavoriteCount },
} = ui;

const {
    selectors: { selectFavoriteIds },
} = favorite;

function useFavoriteCountAll(): void {
    const dispatch = useDispatch();
    const favoriteIds = useSelector(selectFavoriteIds);
    const isRequest = useRef<boolean>(false);

    const getFavoriteCountFromSportEvents = useCallback(
        (
            sportEventType: GQL.SportEventType,
            countType: UI.FavoriteCountType
        ) => {
            const { sportIds } = getDefaultVariablesFavorite();
            const variables: GQL.GetMatchesByEventIdsQueryVariables = {
                sportEventIds: favoriteIds.toArray(),
                // TODO: Refactor Favorite Count: fix counter, avoid obsolete requests on mobile
                sportIds: UserDevice.getInstance()?.isDeviceMobile()
                    ? getBettingOptions().selectedSportIds
                    : sportIds,
            };
            appServiceLocator
                .get('apiClient')
                .makeRequestWithCustomQuery<
                    GetSportEventIdsByFiltersQuery,
                    GQL.GetMatchesByEventIdsQueryVariables,
                    API.Action
                >({
                    query: GET_SPORT_EVENT_IDS_BY_FILTERS as any,
                    variables: {
                        ...variables,
                        matchStatuses: [
                            GQL.SportEventStatus.NOT_STARTED,
                            GQL.SportEventStatus.SUSPENDED,
                            GQL.SportEventStatus.LIVE,
                        ],
                        sportEventTypes: [sportEventType],
                    },
                    action: {
                        type: 'favorite/GET_SPORT_EVENT_IDS_BY_FILTERS',
                        payload: {},
                    },
                })
                .then(({ response }) => {
                    const { matches } = response.data;
                    dispatch(
                        changeFavoriteCount({
                            type: countType,
                            count: matches.length,
                        })
                    );
                })
                .catch(err => {
                    console.error(err);
                });
        },
        [dispatch, favoriteIds]
    );

    const getFavoriteCountMatches = useCallback(() => {
        getFavoriteCountFromSportEvents(GQL.SportEventType.MATCH, 'upcoming');
    }, [getFavoriteCountFromSportEvents]);

    const getFavoriteCountOutright = useCallback(() => {
        getFavoriteCountFromSportEvents(
            GQL.SportEventType.OUTRIGHT,
            'outrights'
        );
    }, [getFavoriteCountFromSportEvents]);

    useEffect(() => {
        if (favoriteIds.size === 0 || isRequest.current) return;

        isRequest.current = true;

        getFavoriteCountMatches();
        getFavoriteCountOutright();
    }, [favoriteIds, getFavoriteCountMatches, getFavoriteCountOutright]);
}

export default useFavoriteCountAll;
